import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"

export type CartProps = SectionBase & {
  title: string
  /** Empty cart message */
  emptyMessage: string
  checkoutLabel: string
  showItemImages: "yes" | "no"
  /**
   * Cart contents are personal (cookie-scoped) and must never be cached into
   * page data. Phase 5 keeps this block as a link to the storefront cart: the
   * button sends the visitor to /[locale]/cart, where the full cart page
   * already handles line items, quantities and checkout. The editor shows a
   * static sample. A live inline cart is planned in a later phase.
   */
  checkoutHref: string
}

export const Cart: ComponentConfig<CartProps> = {
  fields: {
    title: { type: "textarea", contentEditable: true },
    emptyMessage: { type: "text", label: "Empty cart message", contentEditable: true },
    checkoutLabel: { type: "text", label: "Button label", contentEditable: true },
    showItemImages: {
      type: "select",
      options: [
        { label: "Show", value: "yes" },
        { label: "Hide", value: "no" },
      ],
    },
    checkoutHref: {
      type: "text",
      label: "Cart link (leave default)",
    },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  defaultProps: {
    title: "Your cart",
    emptyMessage: "Your cart is empty.",
    checkoutLabel: "Checkout",
    showItemImages: "yes",
    checkoutHref: "/cart",
    background: "none",
    paddingY: "lg",
  },

  render: ({
    title,
    emptyMessage,
    checkoutLabel,
    checkoutHref,
    background,
    paddingY,
  }) => {
    const s = sectionStyle({ background, paddingY })

    return (
      <section style={s}>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 24px" }}>
          <h2 style={{ fontSize: "clamp(1.5rem,3vw,2rem)", fontWeight: 800, marginBottom: 24 }}>{title}</h2>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: 24, background: "#fff" }}>
            <p style={{ color: "#6b7280", textAlign: "center", padding: "24px 0" }}>{emptyMessage}</p>
            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 600 }}>Total</span>
              <span style={{ fontWeight: 800, fontSize: "1.25rem" }}>—</span>
            </div>
            <a
              href={checkoutHref}
              style={{
                display: "block",
                textAlign: "center",
                width: "100%",
                boxSizing: "border-box",
                marginTop: 16,
                padding: 12,
                background: "var(--site-accent, #7c3aed)",
                color: "#fff",
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              {checkoutLabel}
            </a>
          </div>
        </div>
      </section>
    )
  },
}
